import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Plus, 
  Calculator, 
  FileText, 
  Trash2, 
  TrendingUp,
  Building,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DevelopmentAppraisalsPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newAppraisal, setNewAppraisal] = useState({
    client_name: '',
    site_address: '',
    report_reference: ''
  });

  useEffect(() => {
    fetchAppraisals();
  }, []);

  const fetchAppraisals = async () => {
    try {
      const response = await axios.get(`${API}/development-appraisal/appraisals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppraisals(response.data.appraisals || []);
    } catch (error) {
      console.error('Failed to fetch appraisals:', error);
      toast.error('Failed to load appraisals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await axios.post(`${API}/development-appraisal/appraisals`, {
        client_name: newAppraisal.client_name,
        site_inputs: {
          address: newAppraisal.site_address
        },
        report_reference: newAppraisal.report_reference || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Appraisal created successfully');
      setShowCreateDialog(false);
      setNewAppraisal({ client_name: '', site_address: '', report_reference: '' });
      
      // Navigate to the new appraisal
      navigate(`/development-appraisals/${response.data.appraisal.id}`);
    } catch (error) {
      console.error('Failed to create appraisal:', error);
      toast.error('Failed to create appraisal');
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this appraisal?')) return;
    
    try {
      await axios.delete(`${API}/development-appraisal/appraisals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appraisal deleted');
      fetchAppraisals();
    } catch (error) {
      console.error('Failed to delete appraisal:', error);
      toast.error('Failed to delete appraisal');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft', variant: 'secondary', icon: Clock },
      in_review: { label: 'In Review', variant: 'warning', icon: AlertTriangle },
      approved: { label: 'Approved', variant: 'default', icon: CheckCircle },
      final: { label: 'Final', variant: 'success', icon: FileText }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getRiskBadge = (riskBand) => {
    const riskConfig = {
      low: { label: 'Low Risk', className: 'bg-green-100 text-green-800' },
      moderate: { label: 'Moderate Risk', className: 'bg-amber-100 text-amber-800' },
      elevated: { label: 'Elevated Risk', className: 'bg-orange-100 text-orange-800' },
      speculative: { label: 'Speculative', className: 'bg-red-100 text-red-800' }
    };
    
    const config = riskConfig[riskBand] || riskConfig.moderate;
    
    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return `£${value.toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="development-appraisals-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">
              Development Appraisals
            </h1>
            <p className="text-slate-600 mt-1">
              Residual land value calculations and viability assessments
            </p>
          </div>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="create-appraisal-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Appraisal
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-l-4 border-l-teal-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Appraisals</p>
                    <p className="text-2xl font-bold">{appraisals.length}</p>
                  </div>
                  <Calculator className="h-8 w-8 text-teal-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">In Progress</p>
                    <p className="text-2xl font-bold">
                      {appraisals.filter(a => a.status === 'draft' || a.status === 'in_review').length}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Completed</p>
                    <p className="text-2xl font-bold">
                      {appraisals.filter(a => a.status === 'approved' || a.status === 'final').length}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total RLV</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(
                        appraisals.reduce((sum, a) => sum + (a.residual_land_value?.rlv_rounded || 0), 0)
                      )}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Appraisals Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              All Appraisals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : appraisals.length === 0 ? (
              <div className="text-center py-12">
                <Calculator className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No development appraisals yet</p>
                <Button 
                  onClick={() => setShowCreateDialog(true)}
                  variant="outline"
                  data-testid="empty-create-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Appraisal
                </Button>
              </div>
            ) : (
              <Table data-testid="appraisals-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Site Address</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>GDV</TableHead>
                    <TableHead>RLV</TableHead>
                    <TableHead>Risk</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appraisals.map((appraisal) => (
                    <TableRow 
                      key={appraisal.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/development-appraisals/${appraisal.id}`)}
                      data-testid={`appraisal-row-${appraisal.id}`}
                    >
                      <TableCell className="font-medium">
                        {appraisal.report_reference || appraisal.id?.slice(0, 8)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {appraisal.site_inputs?.address || '—'}
                      </TableCell>
                      <TableCell>{appraisal.client_name || '—'}</TableCell>
                      <TableCell className="font-mono">
                        {formatCurrency(appraisal.residual_land_value?.gross_development_value)}
                      </TableCell>
                      <TableCell className="font-mono font-semibold text-teal-700">
                        {formatCurrency(appraisal.residual_land_value?.rlv_rounded)}
                      </TableCell>
                      <TableCell>
                        {appraisal.risk_assessment?.risk_band && 
                          getRiskBadge(appraisal.risk_assessment.risk_band)}
                      </TableCell>
                      <TableCell>{getStatusBadge(appraisal.status)}</TableCell>
                      <TableCell className="text-slate-500">
                        {formatDate(appraisal.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDelete(appraisal.id, e)}
                          data-testid={`delete-appraisal-${appraisal.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent data-testid="create-appraisal-dialog">
            <DialogHeader>
              <DialogTitle>New Development Appraisal</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="client_name">Client Name</Label>
                <Input
                  id="client_name"
                  value={newAppraisal.client_name}
                  onChange={(e) => setNewAppraisal(prev => ({ ...prev, client_name: e.target.value }))}
                  placeholder="Enter client name"
                  data-testid="input-client-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="site_address">Site Address</Label>
                <Input
                  id="site_address"
                  value={newAppraisal.site_address}
                  onChange={(e) => setNewAppraisal(prev => ({ ...prev, site_address: e.target.value }))}
                  placeholder="Enter site address"
                  data-testid="input-site-address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="report_reference">Report Reference (optional)</Label>
                <Input
                  id="report_reference"
                  value={newAppraisal.report_reference}
                  onChange={(e) => setNewAppraisal(prev => ({ ...prev, report_reference: e.target.value }))}
                  placeholder="e.g., DA-2024-001"
                  data-testid="input-report-reference"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate}
                className="bg-teal-600 hover:bg-teal-700"
                disabled={!newAppraisal.client_name || !newAppraisal.site_address}
                data-testid="confirm-create-btn"
              >
                Create Appraisal
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
